import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Skeleton,
  Tooltip,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import Action_icon from "../../images/table/Actions_icon.svg";
import "./RequestTable.css";
import ViewIcon from "../../images/organization/ViewIcon.svg";
import RejectIcon from "../../images/organization/Reject_Icon.svg";
import ApproveIcon from "../../images/organization/Accept_Icon.svg";

const RequestTable = ({
  organizations = [],
  loading = false,
  onApprove,
  onReject,
  onView,
  showRejected = false,
  skeletonRowCount = 5,
}) => {
  const [activeRow, setActiveRow] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: '100%', bottom: 'auto' });
  const actionMenuRef = useRef(null);
  const actionIconRefs = useRef({});
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  useEffect(() => {
    function handleClickOutside(event) {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActiveRow(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleActionClick = (event, rowIndex) => {
    event.stopPropagation();

    // Determine menu position based on available space
    if (activeRow !== rowIndex) {
      const iconElement = actionIconRefs.current[rowIndex];
      if (iconElement) {
        const rect = iconElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;

        // if (spaceBelow < 200) {
        //   setMenuPosition({ top: 'auto', bottom: '100%' });
        // } else {
        //   setMenuPosition({ top: '100%', bottom: 'auto' });
        // }
      }
    }

    setActiveRow(activeRow === rowIndex ? null : rowIndex);
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "ACCEPTED":
        return "green";
      case "REJECTED":
        return "red";
      case "PENDING":
        return "blue";
      default:
        return "black";
    }
  };

  // Skeleton loader rows
  const renderSkeletonRows = () => {
    return Array(skeletonRowCount).fill(0).map((_, rowIndex) => (
      <TableRow
        key={`skeleton-${rowIndex}`}
        sx={{
          padding: "15px 20px",
          "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
          borderBottom: "1px solid #ddd",
          height: "50px",
        }}
      >
        {Array(8).fill(0).map((_, colIndex) => (
          <TableCell
            key={`skeleton-${rowIndex}-${colIndex}`}
            align={colIndex === 7 ? "right" : "left"}
            sx={{ padding: "15px 20px" }}
          >
            {colIndex === 7 ? (
              <Skeleton variant="circular" width={20} height={20} sx={{ ml: "auto" }} />
            ) : (
              <Skeleton variant="text" width={`${Math.floor(Math.random() * 40) + 60}%`} />
            )}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  const headers = ["Organization", "Country", "City", "Contact", "Email", "Phone Number", "Status", "Actions"];

  return (
    <TableContainer
      sx={{
        marginTop: "20px",
        overflow: "auto",
        borderRadius: "10px",
        height: "460px",
        display: "flex",
        flexDirection: "column",

        // Custom scrollbar styling for Chrome
        "&::-webkit-scrollbar": {
          width: "10px",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#647490",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#B7BFCC",
          borderRadius: "10px",
          margin: "5px",
        },

        // For Firefox
        scrollbarWidth: "thin",
        scrollbarColor: "#647490 #B7BFCC",
      }}
    >
      <Table
        sx={{
          flex: 1,
          tableLayout: isSmallScreen ? "auto" : "fixed",
          width: "100%",
          position: "relative",
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#647490",
              position: "relative",
            }}
          >
            {headers.map((header, index) => (
              <TableCell
                key={index}
                sx={{
                  backgroundColor: "#647490",
                  color: "white",
                  fontWeight: "bold",
                  textAlign: header === "Actions" ? "right" : "left",
                  borderTopLeftRadius: index === 0 ? "10px" : 0,
                  borderTopRightRadius: index === headers.length - 1 ? "10px" : 0,
                  borderBottomLeftRadius: index === 0 ? "10px" : 0,
                  borderBottomRightRadius: index === headers.length - 1 ? "10px" : 0,
                  border: "none",
                  padding: "15px 20px",
                }}
              >
                {loading ? (
                  <Skeleton
                    variant="text"
                    sx={{ bgcolor: "rgba(255, 255, 255, 0.3)" }}
                  />
                ) : (
                  t ? t(header) : header
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody sx={{ height: "100%" }}>
          {loading ? (
            // Show skeleton while loading
            renderSkeletonRows()
          ) : organizations.length > 0 ? (
            organizations.map((org, rowIndex) => (
              <TableRow
                key={org.id || rowIndex}
                sx={{
                  padding: "15px 20px",
                  backgroundColor: org.approvalStatus?.toUpperCase() === "REJECTED"
                    ? "#FFEBEE !important"
                    : rowIndex % 2 === 0 ? "#f5f5f5" : "inherit",
                  fontWeight: "bold",
                  color: "black",
                  borderBottom: "1px solid #ddd",
                  height: "50px",
                  "&:hover": {
                    backgroundColor: org.approvalStatus?.toUpperCase() === "REJECTED"
                      ? "#FFEBEE !important"
                      : "#e0e0e0",
                  },
                }}
              >
                <TableCell sx={{ padding: "15px 20px" }}>
                  {org.name}
                </TableCell>
                <TableCell sx={{ padding: "15px 20px" }}>
                  {org.country}
                </TableCell>
                <TableCell sx={{ padding: "15px 20px" }}>
                  {org.city}
                </TableCell>
                <TableCell sx={{ padding: "15px 20px" }}>
                  {org.contact}
                </TableCell>
                <TableCell sx={{ padding: "15px 20px" }} title={org.email}>
                  {org.email}
                </TableCell>
                <TableCell sx={{ padding: "15px 20px" }} title={org.phoneNumber}>
                  {org.phoneNumber}
                </TableCell>
                <TableCell
                  sx={{
                    padding: "15px 20px",
                    color: getStatusColor(org.approvalStatus),
                    fontWeight: "bold"
                  }}
                >
                  {org.approvalStatus}
                </TableCell>
                <TableCell align="right" sx={{ padding: "15px 20px" }}>
                  <div className="action-container" style={{ position: "relative" }}>
                    <div
                      className="action-icon"
                      onClick={(e) => handleActionClick(e, rowIndex)}
                      ref={el => actionIconRefs.current[rowIndex] = el}
                    >
                      <img style={{ width: "20px", height: "20px" }} src={Action_icon} alt="Actions" />
                    </div>
                    {activeRow === rowIndex && (
                      <div
                        className="action-menu"
                        ref={actionMenuRef}
                        style={{
                          ...menuPosition,
                          minWidth: "160px",
                          position: "absolute",
                          right: 0,
                          backgroundColor: "white",
                          borderRadius: "5px",
                          boxShadow: "0 3px 10px rgba(0, 0, 0, 0.2)",
                          zIndex: 100,
                          whiteSpace: "nowrap",
                          border: "solid 1px #A5DCF3",
                        }}
                      >
                        <div
                          className="action-menu-item view"
                          onClick={() => {
                            onView(org);
                            setActiveRow(null);
                          }}
                          style={{
                            padding: "10px 15px",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            borderBottom: "1px solid #f0f0f0"
                          }}
                        >
                          <img
                            src={ViewIcon}
                            alt="view icon"
                            style={{ marginRight: "8px", width: "16px", height: "16px" }}
                          />
                          <span>{t ? t("view") : "View"}</span>
                        </div>

                        {/* Show "Approve" only if status is not ACCEPTED */}
                        {org.approvalStatus?.toUpperCase() !== "ACCEPTED" && (
                          <div
                            className="action-menu-item approve"
                            onClick={() => {
                              onApprove(org.id);
                              setActiveRow(null);
                            }}
                            style={{
                              padding: "10px 15px",
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              borderBottom: org.approvalStatus?.toUpperCase() !== "REJECTED" ? "1px solid #f0f0f0" : "none"
                            }}
                          >
                            <img
                              src={ApproveIcon}
                              alt="approve icon"
                              style={{ marginRight: "8px", width: "16px", height: "16px" }}
                            />
                            <span>{t ? t("approve") : "Approve"}</span>
                          </div>
                        )}

                        {/* Show "Reject" only if status is not REJECTED */}
                        {org.approvalStatus?.toUpperCase() !== "REJECTED" && (
                          <div
                            className="action-menu-item reject"
                            onClick={() => {
                              onReject(org.id);
                              setActiveRow(null);
                            }}
                            style={{
                              padding: "10px 15px",
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}
                          >
                            <img
                              src={RejectIcon}
                              alt="reject icon"
                              style={{ marginRight: "8px", width: "16px", height: "16px" }}
                            />
                            <span>{t ? t("reject") : "Rejectss"}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            // Empty state row
            <TableRow sx={{ height: "100%" }}>
              <TableCell
                colSpan={headers.length}
                sx={{
                  textAlign: "center",
                  verticalAlign: "middle",
                  height: "100%",
                  padding: "15px 20px"
                }}
              >
                {t ? t("No organizations found") : "No organizations found"}
              </TableCell>
            </TableRow>
          )}
          {/* Fill remaining space if needed */}
          {organizations.length > 0 && organizations.length < 9 && (
            <TableRow sx={{ height: `${(9 - organizations.length) * 50}px` }}>
              <TableCell colSpan={headers.length}></TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

RequestTable.propTypes = {
  organizations: PropTypes.array,
  loading: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  showRejected: PropTypes.bool,
  skeletonRowCount: PropTypes.number,
}

export default RequestTable;